import React, {
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  memo,
  type CSSProperties,
  type ReactNode,
} from 'react';
import memoizeOne from 'memoize-one';
import isDeepEqual from 'lodash.isequal';

import Logger from '../../utils/logger';
import Tracer from '../../utils/tracer';
import loadAppManifestUntyped from '../../app-manifest/manifest-loader';
import loadAppAssetsUntyped from '../../app-manifest/assets-loader';
import { registerTranslations } from '../../utils/translations';
import { NOOP } from '../../constants';
import type { Manifest } from '../../shared/types';

// manifest-loader.js / assets-loader.js are not yet migrated to TS, so their
// imports are cast here to the shapes this component actually relies on.
interface TranslationEntry {
  namespace: string;
  data: Record<string, unknown>;
}

interface MicroAppRenderResult {
  unmount: () => void;
}

type SubComponentRenderFn = (
  componentName: string,
  props: Record<string, unknown>,
  node: HTMLElement
) => MicroAppRenderResult;

interface MicroAppModule {
  name?: string;
  create: (
    props: Record<string, unknown>
  ) => Promise<{ render: SubComponentRenderFn } | void>;
  destroy?: () => void;
}

// Cast at the call site (not aliased to a module-scope const) so tests that
// td.replace the untyped module's default export are still picked up here.
// A rest-arg spread (rather than a fixed `(appName, eventBus)` signature)
// keeps the forwarded argument count identical to what the caller passed.
function loadAppManifest(...args: [string, unknown?]): Promise<Manifest> {
  return (loadAppManifestUntyped as (...args: unknown[]) => Promise<Manifest>)(
    ...args
  );
}

function loadAppAssets(
  appName: string,
  manifest: Manifest,
  language: string
): Promise<{ App: MicroAppModule; translationDataList: TranslationEntry[] }> {
  return (
    loadAppAssetsUntyped as (
      appName: string,
      manifest: Manifest,
      language: string
    ) => Promise<{
      App: MicroAppModule;
      translationDataList: TranslationEntry[];
    }>
  )(appName, manifest, language);
}

interface CachedMicroApp {
  name: string;
  create: MicroAppModule['create'];
  destroy?: () => void;
  createdAt?: string;
}

type MicroAppCache = Map<string, CachedMicroApp>;

type MicroAppRenderFn = (
  node: HTMLElement | null,
  props: Record<string, unknown>,
  callback: (result: MicroAppRenderResult) => void
) => void | Promise<void>;

export function createCache(): MicroAppCache {
  return new Map();
}

const EMPTY_STYLE: CSSProperties = {};
const ongoingCreateRequestMap = new Map<
  string,
  ReturnType<MicroAppModule['create']>
>();
const destroyMicroAppCache = new WeakMap<HTMLElement, () => void>();
const renderAppCache = createCache();

function registerAppLocales(localesDataList: TranslationEntry[]): void {
  localesDataList.forEach(({ namespace, data }) => {
    registerTranslations(namespace, data);
  });
}

function destroy(node: HTMLElement | null): void {
  if (!node) return;

  const unmount = destroyMicroAppCache.get(node) || NOOP;
  destroyMicroAppCache.delete(node);

  unmount();
}

function singleComponentMicroAppRender(App: MicroAppModule): MicroAppRenderFn {
  return (node, props, callback) => {
    destroy(node);

    return App.create({ container: node, ...props }).then(() =>
      callback({ unmount: App.destroy || NOOP })
    );
  };
}

async function multiComponentMicroAppRender(
  microApp: string,
  App: MicroAppModule,
  componentName: string,
  globalSettings: Record<string, unknown>
): Promise<MicroAppRenderFn> {
  const ongoingCreateRequest =
    ongoingCreateRequestMap.get(microApp) || App.create(globalSettings);

  ongoingCreateRequestMap.set(microApp, ongoingCreateRequest);

  const created = await ongoingCreateRequest;

  if (!created?.render) {
    throw new Error(
      `${componentName} micro app did not resolve a render function`
    );
  }

  const { render } = created;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (node, { settings, ...props }, callback) => {
    destroy(node);

    const wrapper = render(componentName, props, node as HTMLElement);

    callback({ unmount: wrapper.unmount });
  };
}

function microAppCachedSetup(cache: MicroAppCache) {
  async function importMicroApp(
    microApp: string,
    componentName: string | undefined,
    manifest: Manifest,
    locale: string
  ): Promise<CachedMicroApp> {
    const isMultipleComponentMicroApp = !!componentName;
    const cachedApp = cache.get(microApp);
    const isCached = !!cachedApp;
    const hasMatchingReleaseVersion =
      (isCached && cachedApp?.createdAt === manifest.createdAt) || false;

    if (isCached && hasMatchingReleaseVersion)
      return cachedApp as CachedMicroApp;

    const { App, translationDataList } = await loadAppAssets(
      microApp,
      manifest,
      locale
    );

    registerAppLocales(translationDataList);
    const createFn = isMultipleComponentMicroApp
      ? memoizeOne(App.create, isDeepEqual)
      : App.create;

    const cachedMicroApp: CachedMicroApp = {
      name: microApp,
      create: createFn,
      destroy: App.destroy,
      createdAt: manifest.createdAt,
    };

    cache.set(microApp, cachedMicroApp);

    return cachedMicroApp;
  }

  async function loadRenderFunction(
    microApp: string,
    App: MicroAppModule,
    componentName: string | undefined,
    settings: Record<string, unknown>
  ): Promise<MicroAppRenderFn> {
    const render = await (componentName
      ? multiComponentMicroAppRender(microApp, App, componentName, settings)
      : singleComponentMicroAppRender(App));

    return render;
  }

  return {
    importMicroApp,
    loadRenderFunction,
  };
}

async function loadMicroAppRender(
  microApp: string,
  componentName: string | undefined,
  manifest: Manifest,
  settings: { locale: string; [key: string]: unknown },
  cache: MicroAppCache
): Promise<MicroAppRenderFn> {
  const { locale } = settings;

  if (!locale) {
    throw new Error('Missing settings.locale value!');
  }

  const { importMicroApp, loadRenderFunction } = microAppCachedSetup(cache);

  const App = await importMicroApp(microApp, componentName, manifest, locale);
  const render = await loadRenderFunction(
    microApp,
    App,
    componentName,
    settings
  );

  return render;
}

function cacheDestroyFn(
  node: HTMLElement,
  renderCallbackArgs: MicroAppRenderResult
): void {
  destroyMicroAppCache.set(node, renderCallbackArgs.unmount);
}

interface ContentProps {
  microApp: string;
  componentName?: string;
  manifest: Manifest | null;
  onAction?: (action: unknown) => void;
  settings: { locale: string; [key: string]: unknown };
  apiClient?: unknown;
  el: string;
  cache: MicroAppCache;
  loadingStart: number;
  style: CSSProperties;
  loading: ReactNode;
  [key: string]: unknown;
}

function Content({
  microApp,
  componentName,
  manifest,
  onAction,
  settings,
  apiClient,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  el,
  cache,
  loadingStart,
  style,
  loading,
  ...props
}: ContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isLoading, setLoading] = useState(true);

  const appLoadedCallback =
    (container: HTMLElement) => (renderCallbackArgs: MicroAppRenderResult) => {
      setLoading(false);
      cacheDestroyFn(container, renderCallbackArgs);
      ongoingCreateRequestMap.delete(microApp);

      Tracer.addAction(`${microApp}.micro_app.loaded`, {
        microApp: { loadingTimeInMs: Date.now() - loadingStart },
      });
    };

  useLayoutEffect(
    () => () => {
      destroy(ref.current);
    },
    []
  );

  useEffect(() => {
    async function renderApp(
      container: HTMLElement,
      currentManifest: Manifest
    ) {
      try {
        const render = await loadMicroAppRender(
          microApp,
          componentName,
          currentManifest,
          settings,
          cache
        );

        const microAppProps = {
          useHashHistory: true,
          ...props,
          apiClient,
          settings,
          onAction,
        };

        await render(container, microAppProps, appLoadedCallback(container));
      } catch (error) {
        // Clear any cached in-flight/settled create() request for this
        // microApp so a later retry actually calls create() again instead
        // of replaying the same rejected (or render-less) promise forever.
        ongoingCreateRequestMap.delete(microApp);

        Logger.error('MicroApp Error: Failed to load app assets', {
          app: microApp,
          args: JSON.stringify({ ...props, settings }),
          raisedError: {
            name: error instanceof Error ? error.name : 'Error',
            message: error instanceof Error ? error.message : String(error),
          },
        });
      }
    }

    if (ref.current && manifest) {
      renderApp(ref.current, manifest);
    }
  }, [ref, manifest, props, microApp, componentName, settings, cache]);

  return (
    <div style={{ position: 'relative', ...style }}>
      {isLoading && (
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {loading}
        </div>
      )}
      <div ref={ref} style={{ opacity: isLoading ? 0 : 1 }} />
    </div>
  );
}

export interface MicroAppProps {
  el?: string;
  cache?: MicroAppCache;
  loadingStart?: number;
  name: string;
  style?: CSSProperties;
  loading?: ReactNode;
  settings: { locale: string; [key: string]: unknown };
  apiClient?: unknown;
  onAction?: (action: unknown) => void;
  [key: string]: unknown;
}

function MicroApp({
  el = 'div',
  cache = renderAppCache,
  loadingStart = Date.now(),
  name,
  style = EMPTY_STYLE,
  loading = null,
  ...props
}: MicroAppProps) {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [microApp, componentName] = name.split('/');

  const updateManifest = (newManifest: Manifest) => {
    setManifest(newManifest);
  };

  useEffect(() => {
    loadAppManifest(microApp)
      .then(updateManifest)
      .catch((error: unknown) => {
        Logger.error('MicroApp Error: Failed to load app manifest', {
          app: name,
          args: JSON.stringify(props),
          raisedError: {
            name: error instanceof Error ? error.name : 'Error',
            message: error instanceof Error ? error.message : String(error),
          },
        });
      });
  }, []);

  return (
    <Content
      microApp={microApp}
      componentName={componentName}
      el={el}
      manifest={manifest}
      cache={cache}
      loadingStart={loadingStart}
      style={style}
      loading={loading}
      {...props}
    />
  );
}

export default memo(MicroApp);
